import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'usehooks-ts';
import { toast } from '@kognitos/lattice';
import {
  serializeOutputForClipboardHTML,
  serializeOutputForCopy,
} from '../components/output-card-utils';
import type { EOutputCardVariant, TResolvedValue } from '../types';

/**
 * Copies an output the way its card presents it: rich HTML alongside plain
 * text when the browser supports it, and a toast either way.
 *
 * @returns A function that copies a value and resolves to whether it worked
 */
export function useCopyOutput() {
  const { t } = useTranslation('outputs');
  const [, copyToClipboard] = useCopyToClipboard();

  return async (
    value: TResolvedValue,
    variant: EOutputCardVariant,
  ): Promise<boolean> => {
    const serializedValue = serializeOutputForCopy(value, variant);
    const richHTML = serializeOutputForClipboardHTML(value, variant);
    let copied = false;
    if (
      richHTML &&
      typeof ClipboardItem !== 'undefined' &&
      navigator.clipboard?.write
    ) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': new Blob([serializedValue], { type: 'text/plain' }),
            'text/html': new Blob([richHTML], { type: 'text/html' }),
          }),
        ]);
        copied = true;
      } catch {
        copied = await copyToClipboard(serializedValue);
      }
    } else {
      copied = await copyToClipboard(serializedValue);
    }
    if (copied) {
      toast.success(t('outputs.card.copied'));
    } else {
      toast.error(t('outputs.card.copyFailed'));
    }
    return copied;
  };
}
